import React, { useState } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  MenuItem,
  Select,
  Stack,
  SxProps,
  Theme,
  TextField
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import { Button } from 'system/atoms';
import { TextBlock } from 'common/ui/TextBlock';
import { useToast, ToastType } from 'services/toast';
import { extractErrorMessage } from '../utils/extractErrorMessage';
import {
  useAddDocumentTypeMutation,
  useEditDocumentTypeMutation
} from 'generated-graphql/hooks/apollo-hooks';
import {
  ClassificationRuleType,
  DocumentType,
  DocumentValidationCheck
} from 'generated-graphql/types';
import {
  CLASSIFICATION_RULE_SUGGESTIONS,
  CLASSIFICATION_SUGGESTION_SLOTS,
  DATE_RANGE_UNIT_OPTIONS,
  DEFAULT_DATE_RANGE,
  DateRangeUnit,
  DOCUMENT_VALIDATION_LABELS,
  PRIMARY_VALIDATION_CHECKS
} from '../constants';
import {
  parseValidationSettings,
  RelativeDateRange,
  serialiseValidationSettings
} from '../utils/validationSettings';

const styles = {
  container: { pb: 4 },
  header: { mb: 3 },
  body: { gap: 3, flex: 1 },
  fieldLabel: { mb: 1 },
  chipsBox: (chipsFocused: boolean): SxProps<Theme> => ({
    background: 'white',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 1,
    padding: '16.5px 14px',
    border: chipsFocused
      ? (theme) => theme.palette.border.large.primary.main
      : (theme) => theme.palette.border.small.grey.dark,
    borderRadius: 1
  }),
  chip: {
    '& .MuiChip-deleteIcon': {
      color: 'primary.main',
      '&:hover': { color: 'primary.main' }
    }
  },
  chipInput: { flex: 1, minWidth: 120 },
  suggestionsStack: { flexWrap: 'wrap', gap: 1, mt: 1 },
  suggestionChip: {
    '& .MuiChip-deleteIcon': {
      color: 'primary.contrastText',
      '&:hover': { color: 'primary.contrastText' }
    }
  },
  checksStack: { gap: 0.5 },
  dateRangeStack: { pl: 4, gap: 1, mt: 1, alignItems: 'center' },
  dateRangeNumber: { width: 80, '& .MuiOutlinedInput-root': { height: 52 } },
  dateRangeSelect: { minWidth: 140, height: 52 },
  archiveIcon: { mr: 1 },
  footer: { gap: 1, mt: 4 }
};

type Props = {
  organizationId: string;
  documentType?: Pick<
    DocumentType,
    | 'id'
    | 'name'
    | 'isUsedInCampaign'
    | 'validation'
    | 'validationSettings'
    | 'classificationRules'
  >;
  onClose: () => void;
  onSaved: (documentType: DocumentType) => void;
  onArchiveRequested?: () => void;
};

type DateRangeState = {
  enabled: boolean;
  value: number;
  unit: DateRangeUnit;
};

const dateRangeFromSetting = (
  setting: RelativeDateRange | undefined
): DateRangeState => ({
  enabled: Boolean(setting),
  value: setting?.value ?? DEFAULT_DATE_RANGE.value,
  unit: setting?.unit ?? DEFAULT_DATE_RANGE.unit
});

export const DocumentTypeDrawer: React.FC<Props> = ({
  organizationId,
  documentType,
  onClose,
  onSaved,
  onArchiveRequested
}) => {
  const isEdit = Boolean(documentType?.id);
  const isNameLocked = Boolean(documentType?.isUsedInCampaign);
  const initialSettings = parseValidationSettings(
    documentType?.validationSettings
  );

  const [name, setName] = useState(documentType?.name ?? '');
  const [chips, setChips] = useState<string[]>(
    (documentType?.classificationRules ?? [])
      .map((r) => r.value)
      .filter((v): v is string => Boolean(v))
  );
  const [chipDraft, setChipDraft] = useState('');
  const [chipsFocused, setChipsFocused] = useState(false);
  const [dismissedSuggestions, setDismissedSuggestions] = useState<Set<string>>(
    new Set()
  );
  const [checks, setChecks] = useState<Set<DocumentValidationCheck>>(
    new Set(documentType?.validation ?? [])
  );
  const [issuedAfter, setIssuedAfter] = useState<DateRangeState>(
    dateRangeFromSetting(initialSettings.issuedAfter)
  );
  const [validFor, setValidFor] = useState<DateRangeState>(
    dateRangeFromSetting(initialSettings.validFor)
  );

  const { showToast } = useToast();
  const [addDocumentType, { loading: creating }] = useAddDocumentTypeMutation();
  const [editDocumentType, { loading: updating }] =
    useEditDocumentTypeMutation();
  const saving = creating || updating;

  const toggleCheck = (check: DocumentValidationCheck) => {
    setChecks((prev) => {
      const next = new Set(prev);
      if (next.has(check)) next.delete(check);
      else next.add(check);
      return next;
    });
  };

  const addChip = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setChips((prev) =>
      prev.some((c) => c.toLowerCase() === trimmed.toLowerCase())
        ? prev
        : [...prev, trimmed]
    );
  };

  const commitChip = () => {
    addChip(chipDraft);
    setChipDraft('');
  };

  const removeChip = (value: string) => {
    setChips((prev) => prev.filter((c) => c !== value));
  };

  // The user gets a fixed pool of common "must contain" fields as a suggestion
  // strip beneath the input. Show the first N that aren't already selected or
  // dismissed so picking (or removing) one shifts the next unused entry from
  // the ordered pool into the vacated slot.
  const selectedChipsLower = new Set(chips.map((c) => c.toLowerCase()));
  const visibleSuggestions = CLASSIFICATION_RULE_SUGGESTIONS.filter(
    (s) =>
      !selectedChipsLower.has(s.toLowerCase()) && !dismissedSuggestions.has(s)
  ).slice(0, CLASSIFICATION_SUGGESTION_SLOTS);

  const dismissSuggestion = (suggestion: string) =>
    setDismissedSuggestions((prev) => {
      const next = new Set(prev);
      next.add(suggestion);
      return next;
    });

  const setIssuedAfterEnabled = (enabled: boolean) =>
    setIssuedAfter((prev) => ({ ...prev, enabled }));
  const setValidForEnabled = (enabled: boolean) =>
    setValidFor((prev) => ({ ...prev, enabled }));

  const showError = (error: unknown) =>
    showToast({
      message: extractErrorMessage(error),
      type: ToastType.Error
    });

  const handleSubmit = async () => {
    if (!name.trim()) return;

    // `checks` is authoritative for enums with a top-level checkbox
    // (PRIMARY_VALIDATION_CHECKS) and the date-range widgets are authoritative
    // for enums they exclusively control. `CheckExpirationDate` has both a
    // top-level checkbox AND the `validFor` date-range, so either can enable
    // it — add here without deleting so the top-level checkbox still works.
    // `CheckIssueDate` has NO top-level checkbox, so its state must come from
    // `issuedAfter.enabled` alone — set authoritatively (add-or-delete) so
    // unchecking the date-range widget removes the enum even when it was in
    // the initial `checks` snapshot loaded from the DB.
    const validation = new Set(checks);
    if (validFor.enabled)
      validation.add(DocumentValidationCheck.CheckExpirationDate);
    if (issuedAfter.enabled)
      validation.add(DocumentValidationCheck.CheckIssueDate);
    else validation.delete(DocumentValidationCheck.CheckIssueDate);

    const validationSettings = serialiseValidationSettings({
      validFor: validFor.enabled
        ? { value: validFor.value, unit: validFor.unit }
        : undefined,
      issuedAfter: issuedAfter.enabled
        ? { value: issuedAfter.value, unit: issuedAfter.unit }
        : undefined
    });

    const classificationRules = chips.map((value) => ({
      type: ClassificationRuleType.IncludesField,
      value
    }));

    const commonInput = {
      name: name.trim(),
      classificationRules,
      validation: [...validation],
      validationSettings
    };

    try {
      if (isEdit && documentType?.id) {
        const { data } = await editDocumentType({
          variables: { input: { id: documentType.id, ...commonInput } }
        });
        if (!data?.editDocumentType)
          throw new Error('editDocumentType returned null');
        showToast({
          message: 'Validation checks updated',
          type: ToastType.Success
        });
        onSaved(data.editDocumentType);
        return;
      }
      const { data } = await addDocumentType({
        variables: { input: { organizationId, ...commonInput } }
      });
      if (!data?.addDocumentType)
        throw new Error('addDocumentType returned null');
      showToast({
        message: 'New document type added',
        type: ToastType.Success
      });
      onSaved(data.addDocumentType);
    } catch (error) {
      showError(error);
    }
  };

  const primaryLabel = isEdit
    ? isNameLocked
      ? 'Confirm changes'
      : 'Confirm changes'
    : 'Add document';
  const heading = isEdit
    ? documentType?.name || 'Edit document'
    : 'Add new document type';

  return (
    <Box sx={styles.container}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        sx={styles.header}
      >
        <TextBlock variant="drawer" title={heading} />
        <Button variant="link" onClick={onClose} aria-label="Close drawer">
          <CloseIcon fontSize="small" />
        </Button>
      </Stack>

      <Stack sx={styles.body}>
        <Box>
          <TextBlock
            variant="field"
            title="Document name"
            subtitle={
              isNameLocked
                ? 'Cannot be edited because it has been used in a workflow.'
                : 'Name cannot be changed after it has been added to a workflow.'
            }
            sx={styles.fieldLabel}
          />
          <TextField
            fullWidth
            size="small"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={isNameLocked}
            placeholder="e.g. Driving licence"
          />
        </Box>

        <Box>
          <TextBlock
            variant="field"
            title="Document must contain (optional)"
            sx={styles.fieldLabel}
          />
          {/* Mirrors the container + chip styling of `common/form/components/ChipInput`
              which the Screening questions "Ask for document(s)" input uses:
              white bg, focus border in primary, `Chip color="primary"` with
              the delete icon tinted the same. Behaviour matches too — Enter
              commits, empty-Backspace removes the last chip. */}
          <Box
            onFocus={() => setChipsFocused(true)}
            onBlur={() => setChipsFocused(false)}
            sx={styles.chipsBox(chipsFocused)}
          >
            {chips.map((value) => (
              <Chip
                key={value}
                label={value}
                onDelete={() => removeChip(value)}
                color="primary"
                deleteIcon={<CloseIcon />}
                sx={styles.chip}
              />
            ))}
            <TextField
              variant="standard"
              value={chipDraft}
              onChange={(e) => setChipDraft(e.target.value)}
              onBlur={commitChip}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ',') {
                  e.preventDefault();
                  commitChip();
                } else if (
                  e.key === 'Backspace' &&
                  !chipDraft &&
                  chips.length
                ) {
                  removeChip(chips[chips.length - 1]);
                }
              }}
              InputProps={{ disableUnderline: true }}
              sx={styles.chipInput}
            />
          </Box>
          {visibleSuggestions.length > 0 && (
            <Stack direction="row" sx={styles.suggestionsStack}>
              {visibleSuggestions.map((suggestion) => (
                <Chip
                  key={suggestion}
                  label={suggestion}
                  aria-label={`Add ${suggestion}`}
                  color="primary"
                  onClick={() => addChip(suggestion)}
                  onDelete={() => dismissSuggestion(suggestion)}
                  deleteIcon={
                    <CloseIcon aria-label={`Dismiss ${suggestion}`} />
                  }
                  sx={styles.suggestionChip}
                />
              ))}
            </Stack>
          )}
        </Box>

        <Box>
          <TextBlock
            variant="field"
            title="Validation checks (optional)"
            sx={styles.fieldLabel}
          />
          <Stack sx={styles.checksStack}>
            {PRIMARY_VALIDATION_CHECKS.map((check) => (
              <FormControlLabel
                key={check}
                control={
                  <Checkbox
                    checked={checks.has(check)}
                    onChange={() => toggleCheck(check)}
                  />
                }
                label={DOCUMENT_VALIDATION_LABELS[check]}
              />
            ))}
            <DateRangeCheckbox
              label="Document was issued in the last"
              state={issuedAfter}
              onEnabledChange={setIssuedAfterEnabled}
              onValueChange={(value) =>
                setIssuedAfter((prev) => ({ ...prev, value }))
              }
              onUnitChange={(unit) =>
                setIssuedAfter((prev) => ({ ...prev, unit }))
              }
            />
            <DateRangeCheckbox
              label="Document is valid for at least"
              state={validFor}
              onEnabledChange={setValidForEnabled}
              onValueChange={(value) =>
                setValidFor((prev) => ({ ...prev, value }))
              }
              onUnitChange={(unit) =>
                setValidFor((prev) => ({ ...prev, unit }))
              }
            />
          </Stack>
        </Box>
      </Stack>

      <Stack sx={styles.footer}>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={!name.trim() || saving}
        >
          {primaryLabel}
        </Button>
        {isEdit && isNameLocked && onArchiveRequested && (
          <Button variant="secondary" onClick={onArchiveRequested}>
            <Inventory2Icon fontSize="small" sx={styles.archiveIcon} />
            Archive
          </Button>
        )}
        <Button variant="tertiary" onClick={onClose}>
          Cancel
        </Button>
      </Stack>
    </Box>
  );
};

const DateRangeCheckbox: React.FC<{
  label: string;
  state: DateRangeState;
  onEnabledChange: (enabled: boolean) => void;
  onValueChange: (value: number) => void;
  onUnitChange: (unit: DateRangeUnit) => void;
}> = ({ label, state, onEnabledChange, onValueChange, onUnitChange }) => (
  <Box>
    <FormControlLabel
      control={
        <Checkbox
          checked={state.enabled}
          onChange={(e) => onEnabledChange(e.target.checked)}
        />
      }
      label={label}
    />
    {/* Inputs remain interactive regardless of the checkbox state — ticking
        the box just adds the corresponding validation to the persisted set. */}
    <Stack direction="row" sx={styles.dateRangeStack}>
      <TextField
        type="number"
        size="small"
        value={state.value}
        inputProps={{ min: 1 }}
        onChange={(e) =>
          onValueChange(Math.max(1, Number(e.target.value) || 1))
        }
        sx={styles.dateRangeNumber}
      />
      <Select
        size="small"
        value={state.unit}
        onChange={(e) => onUnitChange(e.target.value as DateRangeUnit)}
        sx={styles.dateRangeSelect}
        // DrawerV2 sits at zIndex 1400 (higher than MUI's default popover
        // zIndex of 1300), so the portal-rendered Select menu ends up
        // behind the drawer's paper. Rendering the menu inline keeps it in
        // the drawer's own stacking context so all three options are
        // clickable.
        MenuProps={{ disablePortal: true }}
      >
        {DATE_RANGE_UNIT_OPTIONS.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </Stack>
  </Box>
);
